import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, CheckCircle, DollarSign, Download, FileText, Percent, XCircle } from 'lucide-react';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useTechs } from '@/hooks/useReferenceData';
import { useTechReport } from '@/hooks/useTechReport';
import { useTechCommissionReport } from '@/hooks/useTechCommissionReport';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useVisibility } from '@/hooks/useVisibility';
import { useToast } from '@/hooks/use-toast';
import { generateSingleTechCommissionHtml, downloadHtmlReport } from '@/utils/techCommissionHtmlGenerator';
import type { TechPerformanceMetrics } from '@/types/reports';
import { MetricCard } from './MetricCard';

interface SingleTechReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
}

const csvCell = (v: string | number | null | undefined): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** useTechReport groups by job location first, so one tech can appear under several metros. */
function mergeTechRows(rows: TechPerformanceMetrics[]): TechPerformanceMetrics | null {
  if (rows.length === 0) return null;
  const merged = rows.reduce<TechPerformanceMetrics>(
    (acc, r) => ({
      ...acc,
      total_jobs: acc.total_jobs + r.total_jobs,
      closed_jobs: acc.closed_jobs + r.closed_jobs,
      canceled_jobs: acc.canceled_jobs + r.canceled_jobs,
      total_parts_used: acc.total_parts_used + r.total_parts_used,
      total_tolls: acc.total_tolls + r.total_tolls,
      cash_payments: acc.cash_payments + r.cash_payments,
      card_payments: acc.card_payments + r.card_payments,
      bank_transfer_payments: acc.bank_transfer_payments + r.bank_transfer_payments,
      total_revenue: acc.total_revenue + r.total_revenue,
      jobs: [...acc.jobs, ...r.jobs],
      cancelled_jobs_details: [...acc.cancelled_jobs_details, ...r.cancelled_jobs_details],
    }),
    {
      ...rows[0],
      total_jobs: 0,
      closed_jobs: 0,
      canceled_jobs: 0,
      total_parts_used: 0,
      total_tolls: 0,
      cash_payments: 0,
      card_payments: 0,
      bank_transfer_payments: 0,
      total_revenue: 0,
      jobs: [],
      cancelled_jobs_details: [],
    },
  );
  merged.closed_percentage = merged.total_jobs > 0 ? (merged.closed_jobs / merged.total_jobs) * 100 : 0;
  merged.avg_ticket = merged.closed_jobs > 0 ? merged.total_revenue / merged.closed_jobs : 0;
  return merged;
}

/**
 * Technicians tab for non-admin users: one technician at a time. No
 * cross-technician comparison, company totals or override income here.
 */
export const SingleTechReport = ({ startDate, endDate, timezone: propTimezone }: SingleTechReportProps) => {
  const { timezone: contextTimezone, formatDate } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const navigate = useNavigate();
  const { toast } = useToast();
  const tax = useCompanyTax();
  const { locale, formatMoney: formatCurrency } = useCompanyLocale();

  const [techId, setTechId] = useState<string | null>(null);
  const { data: techs = [] } = useTechs();
  // Commission is technician pay: admins only (visibility policy 2026-09-08).
  const { canSeeTechPay } = useVisibility();
  const { data: metroData, isLoading } = useTechReport(startDate, endDate, timezone, techId);
  const { data: commissionData } = useTechCommissionReport(startDate, endDate, timezone, techId);

  const selectedTech = techs.find((t) => t.id === techId) ?? null;

  const tech = useMemo(
    () =>
      techId && metroData
        ? mergeTechRows(metroData.flatMap((m) => m.technicians).filter((t) => t.tech_id === techId))
        : null,
    [metroData, techId],
  );
  const commissionTech = commissionData?.technicians.find((t) => t.techId === techId) ?? null;
  const commissionByJob = useMemo(
    () => new Map((commissionTech?.jobs ?? []).map((j) => [j.jobId, j])),
    [commissionTech],
  );
  const totalCommission = commissionTech?.totalGrossCommission ?? 0;

  const handleExportCsv = () => {
    if (!tech) return;
    const headers = [
      'Job Number',
      'Customer Name',
      'Service Date',
      'Date Posted',
      'Status',
      'Revenue',
      'Parts Cost',
      'Toll Cost',
      'Commission',
      'Payment Status',
      'Cancellation Reason',
    ];
    const COMMISSION_COL = 8;
    const rows = [
      ...tech.jobs.map((job) => [
        job.job_number,
        job.customer_name,
        job.scheduled_date ? formatDate(new Date(job.scheduled_date), 'yyyy-MM-dd') : '',
        job.closed_at ? formatDate(new Date(job.closed_at), 'yyyy-MM-dd') : '',
        job.status,
        (job.invoice_total || 0).toFixed(2),
        (job.total_parts_cost || 0).toFixed(2),
        (job.toll_cost || 0).toFixed(2),
        (commissionByJob.get(job.id)?.grossCommission ?? 0).toFixed(2),
        job.payment_status,
        '',
      ]),
      ...tech.cancelled_jobs_details.map((job) => [
        job.job_number,
        job.customer_name,
        job.scheduled_date ? formatDate(new Date(job.scheduled_date), 'yyyy-MM-dd') : '',
        job.cancelled_at ? formatDate(new Date(job.cancelled_at), 'yyyy-MM-dd') : '',
        job.status,
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        'n/a',
        job.cancellation_reason || '',
      ]),
    ];
    const csvContent = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tech.tech_name.replace(/\s+/g, '_')}-jobs-${formatDate(startDate, 'yyyy-MM-dd')}-to-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadHtml = () => {
    if (!commissionData || !commissionTech) {
      toast({
        title: 'No data available',
        description: 'No completed jobs for this technician in the selected period',
        variant: 'destructive',
      });
      return;
    }

    const html = generateSingleTechCommissionHtml(
      commissionTech,
      commissionData.startDate,
      commissionData.endDate,
      commissionData.companyName,
      tax,
      locale,
      timezone,
    );
    const filename = `${commissionTech.techName.replace(/\s+/g, '_')}_Commission_Report_${formatDate(startDate, 'yyyy-MM-dd')}_to_${formatDate(endDate, 'yyyy-MM-dd')}.html`;
    downloadHtmlReport(html, filename);

    toast({
      title: 'Report downloaded',
      description: `${commissionTech.techName}'s commission report downloaded successfully`,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Technician report</CardTitle>
              <CardDescription>{canSeeTechPay ? "Jobs, parts, tolls and commission for one technician" : "Jobs, parts and tolls for one technician"}</CardDescription>
            </div>
            <Select value={techId ?? ''} onValueChange={setTechId}>
              <SelectTrigger className="h-9 w-full sm:w-[260px]" aria-label="Technician">
                <SelectValue placeholder="Select a technician" />
              </SelectTrigger>
              <SelectContent>
                {techs.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        {!techId && (
          <CardContent>
            <p className="py-8 text-center text-muted-foreground">
              Pick a technician to see their jobs for the selected period.
            </p>
          </CardContent>
        )}
      </Card>

      {techId && isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {techId && !isLoading && !tech && (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-muted-foreground">
              No activity for {selectedTech?.name ?? 'this technician'} in the selected date range.
            </p>
          </CardContent>
        </Card>
      )}

      {tech && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <MetricCard
              title="Jobs"
              value={tech.total_jobs}
              icon={Briefcase}
              tooltip="Completed and cancelled jobs assigned to this technician in the period"
            />
            <MetricCard title="Completed" value={tech.closed_jobs} icon={CheckCircle} valueColor="text-success" />
            <MetricCard title="Close rate" value={`${tech.closed_percentage.toFixed(1)}%`} icon={Percent} />
            <MetricCard
              title="Revenue"
              value={formatCurrency(tech.total_revenue)}
              subtitle={`Avg ticket ${formatCurrency(tech.avg_ticket)}`}
              icon={DollarSign}
              valueColor="text-primary"
            />
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>{tech.tech_name}</CardTitle>
                  <CardDescription>Completed jobs ({tech.jobs.length})</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleExportCsv} variant="outline" size="sm">
                    <Download className="mr-2 h-4 w-4" />
                    Export jobs CSV
                  </Button>
                  {canSeeTechPay && (
                    <Button onClick={handleDownloadHtml} variant="default" size="sm">
                      <FileText className="mr-2 h-4 w-4" />
                      Commission report
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/50 p-3 md:grid-cols-5">
                <div>
                  <p className="text-xs text-muted-foreground">Cash</p>
                  <p className="font-semibold">{formatCurrency(tech.cash_payments)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Card</p>
                  <p className="font-semibold">{formatCurrency(tech.card_payments)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Bank transfer</p>
                  <p className="font-semibold">{formatCurrency(tech.bank_transfer_payments)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Parts</p>
                  <p className="font-semibold">{formatCurrency(tech.total_parts_used)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tolls</p>
                  <p className="font-semibold">{formatCurrency(tech.total_tolls)}</p>
                </div>
              </div>

              {tech.jobs.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No completed jobs</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs font-semibold text-muted-foreground">
                        <th className="px-2 py-2 text-left">Job</th>
                        <th className="px-2 py-2 text-left">Customer</th>
                        <th className="px-2 py-2 text-left">Posted</th>
                        <th className="px-2 py-2 text-right">Revenue</th>
                        <th className="px-2 py-2 text-right">Parts</th>
                        <th className="px-2 py-2 text-right">Tolls</th>
                        {canSeeTechPay && <th className="px-2 py-2 text-right">Commission</th>}
                        <th className="px-2 py-2 text-center">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tech.jobs.map((job) => (
                        <tr
                          key={job.id}
                          className="cursor-pointer border-b hover:bg-muted/50"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                        >
                          <td className="px-2 py-2 font-mono">{job.job_number}</td>
                          <td className="px-2 py-2">
                            <CustomerLink customerId={job.customer_id} name={job.customer_name} />
                          </td>
                          <td className="px-2 py-2 text-muted-foreground">
                            {job.closed_at ? formatDate(new Date(job.closed_at), 'MMM d, yyyy') : '-'}
                          </td>
                          <td className="px-2 py-2 text-right font-medium tabular-nums">
                            {formatCurrency(job.invoice_total || 0)}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            {formatCurrency(job.total_parts_cost || 0)}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(job.toll_cost || 0)}</td>
                          {canSeeTechPay && (
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatCurrency(commissionByJob.get(job.id)?.grossCommission ?? 0)}
                            </td>
                          )}
                          <td className="px-2 py-2 text-center">
                            <Badge
                              variant={
                                job.payment_status === 'paid'
                                  ? 'default'
                                  : job.payment_status === 'partially_paid'
                                    ? 'secondary'
                                    : 'outline'
                              }
                            >
                              {job.payment_status.replace('_', ' ')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 font-semibold">
                        <td className="px-2 py-2" colSpan={3}>
                          Total
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(tech.total_revenue)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(tech.total_parts_used)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(tech.total_tolls)}</td>
                        {canSeeTechPay && <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(totalCommission)}</td>}
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {tech.cancelled_jobs_details.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <XCircle className="h-4 w-4 text-destructive" />
                  Cancelled jobs ({tech.cancelled_jobs_details.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {tech.cancelled_jobs_details.map((job) => (
                  <div
                    key={job.id}
                    className="cursor-pointer rounded-lg border p-3 transition-colors hover:bg-muted/50"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-mono text-sm font-medium">{job.job_number}</p>
                        <CustomerLink
                          customerId={job.customer_id}
                          name={job.customer_name}
                          className="block text-sm text-muted-foreground"
                        />
                      </div>
                      {job.cancelled_at && (
                        <p className="text-xs text-muted-foreground">{formatDate(new Date(job.cancelled_at), 'PP')}</p>
                      )}
                    </div>
                    <p className="mt-2 border-t pt-2 text-sm">
                      <span className="text-muted-foreground">Reason: </span>
                      <span className="font-medium">{job.cancellation_reason || 'Not specified'}</span>
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
